import {FILTER_KEYS, type FilterQuery, USER_FILTERS} from '@github-ui/filter-core'
import {ISSUE_TYPE_FILTER_KEY} from '@github-ui/issue-type-filter-provider'
import {useCallback, useState} from 'react'

import type {ClientMemexWorkflow, MemexWorkflow} from '../../../api/workflows/contracts'
import {isArchiveWorkflow, isAutoAddWorkflow} from '../../../helpers/workflow-utilities'
import {FilterQueryResources, WorkflowResources} from '../../../strings'
import {FilterKeys as MemexFilterKeys} from '../../filter-bar/filter-providers'

export const useWorkflowQueryValidation = (workflow: ClientMemexWorkflow, isDirty: boolean) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const isAutoAdd = isAutoAddWorkflow(workflow)

  const onValidation = useCallback(
    (validationMessages: Array<string>, filterQuery: FilterQuery) => {
      const invalidKeys = unsupportedFilterKeys(filterQuery)
      if (isDirty && filterQuery.raw.trim().length === 0) {
        if (isAutoAdd) {
          setErrorMessage(WorkflowResources.emptyFilterAutoAdd)
        } else {
          setErrorMessage(WorkflowResources.emptyFilterDefault)
        }
      } else if (validationMessages.length === 0 && invalidKeys.length > 0) {
        // Only warn for unsupported fields if there are no other validation messages
        setErrorMessage(FilterQueryResources.unknownFieldFilters(invalidKeys))
      } else if (errorMessage) {
        setErrorMessage(undefined)
      }
    },
    [isDirty, errorMessage, isAutoAdd],
  )
  return {onValidation, errorMessage}
}

const keyPattern = new RegExp(/([^-][\w.#-]+):/)
const unsupportedFilterKeys = (filterQuery: FilterQuery): Array<string> => {
  // Collect unquoted text blocks that match the filter key pattern
  // e.g., abc:xyz or -abc:xyz will match 'abc' as a key, but "abc:xyz" will not
  const textBlocksWithKeyPattern = filterQuery.blocks.filter(
    b => b.type === 'text' && b.raw.match(keyPattern) && !b.raw.match(/"|'/),
  )
  // Extract and return the key values
  const keys = textBlocksWithKeyPattern.map(b => b.raw.match(keyPattern)?.[1])
  return keys.filter(b => b != null)
}

export const getSupportedProviderKeys = (workflow: MemexWorkflow) => {
  const baseProviders = [FILTER_KEYS.reason.key, FILTER_KEYS.is.key]
  if (isAutoAddWorkflow(workflow)) {
    return [
      ...baseProviders,
      USER_FILTERS.assignee.key,
      FILTER_KEYS.milestone.key,
      FILTER_KEYS.label.key,
      ISSUE_TYPE_FILTER_KEY.key,
    ]
  }
  if (isArchiveWorkflow(workflow)) {
    return [...baseProviders, MemexFilterKeys.lastUpdated.key, FILTER_KEYS.updated.key]
  }
  return baseProviders
}
